import { useMemo, useState } from "react";
import { AppConfigurationVersion, VersionMetrics, TimelineFilters } from "@/types/application";

export const useVersionMetrics = (versions: AppConfigurationVersion[]) => {
  const [filters, setFilters] = useState<TimelineFilters>({
    dateRange: { start: null, end: null },
    changeTypes: {
      keyChanges: true,
      customerChanges: true,
      activationChanges: true,
      nameChanges: true
    },
    showMilestonesOnly: false
  });

  const metrics = useMemo(() => {
    return versions.map(version => {
      const changes = version.changeDetails?.changes || [];
      
      const keyChanges = changes.filter(c => c.field === 'keys');
      const customerChanges = changes.filter(c => c.field === 'customers');
      const activationChanges = changes.filter(c => c.field === 'isActive');
      
      // Calculate customer changes
      let customersAdded = 0;
      let customersRemoved = 0;
      
      customerChanges.forEach(change => {
        const oldCount = (change.oldValue as string[])?.length || 0;
        const newCount = (change.newValue as string[])?.length || 0;
        const diff = newCount - oldCount;
        
        if (diff > 0) {
          customersAdded = diff;
        } else if (diff < 0) {
          customersRemoved = Math.abs(diff);
        }
      });
      
      return {
        date: version.createdAt,
        versionNumber: version.versionNumber,
        keysAdded: keyChanges.filter(c => c.action === 'added').length,
        keysModified: keyChanges.filter(c => c.action === 'modified').length,
        keysRemoved: keyChanges.filter(c => c.action === 'removed').length,
        totalKeys: version.configurationData.keys.length,
        customersAdded,
        customersRemoved,
        totalCustomers: version.customers.length,
        activationChanged: activationChanges.length > 0,
        isActive: version.isActive,
        isMilestone: version.isMilestone,
        changeSummary: version.changeSummary || 'No changes',
        versionId: version.id
      } as VersionMetrics;
    });
  }, [versions]);

  const filteredMetrics = useMemo(() => {
    return metrics.filter(metric => {
      // Date range filter
      if (filters.dateRange.start && new Date(metric.date) < filters.dateRange.start) {
        return false;
      }
      if (filters.dateRange.end && new Date(metric.date) > filters.dateRange.end) {
        return false;
      }

      // Milestone filter
      if (filters.showMilestonesOnly && !metric.isMilestone) {
        return false;
      }

      // Change type filters - at least one filter must be active
      const hasKeyChanges = metric.keysAdded > 0 || metric.keysModified > 0 || metric.keysRemoved > 0;
      const hasCustomerChanges = metric.customersAdded !== 0 || metric.customersRemoved !== 0;
      const hasActivationChange = metric.activationChanged;
      
      // Check if this metric should be shown based on active filters
      let shouldShow = false;
      
      if (filters.changeTypes.keyChanges && hasKeyChanges) {
        shouldShow = true;
      }
      if (filters.changeTypes.customerChanges && hasCustomerChanges) {
        shouldShow = true;
      }
      if (filters.changeTypes.activationChanges && hasActivationChange) {
        shouldShow = true;
      }
      
      // If no specific change type filter is active, show all
      if (!filters.changeTypes.keyChanges && 
          !filters.changeTypes.customerChanges && 
          !filters.changeTypes.activationChanges &&
          !filters.changeTypes.nameChanges) {
        shouldShow = true;
      }

      return shouldShow;
    });
  }, [metrics, filters]);

  return {
    metrics: filteredMetrics,
    allMetrics: metrics,
    filters,
    setFilters,
    summary: {
      totalVersions: versions.length,
      totalKeyChanges: metrics.reduce((sum, m) => sum + m.keysAdded + m.keysModified + m.keysRemoved, 0),
      totalCustomerChanges: metrics.reduce((sum, m) => sum + Math.abs(m.customersAdded) + Math.abs(m.customersRemoved), 0),
      activationChanges: metrics.filter(m => m.activationChanged).length,
      milestones: metrics.filter(m => m.isMilestone).length
    }
  };
};
